import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Job,
  JobApplication,
  JobApplicationStatus,
  User,
} from '@core/generated/gql-types';
import { useJobApplicationStore } from '@core/jobApplication/useJobApplicationStore';
import { useWorkerStore } from '@core/worker/useWorkerStore';
import {
  TemplateTerm,
  useTemplateStore,
} from '@core/template/useTemplateStore';
import WorkerSearchbar from './workerSearchbar/WorkerSearchbar';
import WorkerApplication from './WorkerApplication';

interface AppliedWorkersProps {
  job: Job;
  jobApplications: JobApplication[];
  openWorkerSearchModal: boolean;
  setLoading: (loading: boolean) => void;
  setOpenWorkerSearchModal: (open: boolean) => void;
}

const AppliedWorkers = ({
  job,
  jobApplications,
  openWorkerSearchModal,
  setLoading,
  setOpenWorkerSearchModal,
}: AppliedWorkersProps) => {
  const { respondToApplication } = useJobApplicationStore();

  const [appliedWorkers, setAppliedWorkers] = useState<User[]>([]);

  const { workers } = useWorkerStore();

  const { getTerm } = useTemplateStore();

  useEffect(() => {
    // Index workers by id for O(1) lookups while preserving job application order.
    const workerById = new Map(
      workers.map((worker) => [worker.id, worker] as const),
    );

    const orderedAppliedWorkers = jobApplications
      .map((jobApplication) => workerById.get(jobApplication.userId))
      .filter((worker): worker is User => Boolean(worker));

    // De-duplicate workers while keeping the earliest application order.
    const uniqueOrderedAppliedWorkers: User[] = [];
    const seenWorkerIds = new Set<string>();

    for (const worker of orderedAppliedWorkers) {
      if (seenWorkerIds.has(worker.id)) {
        continue;
      }
      seenWorkerIds.add(worker.id);
      uniqueOrderedAppliedWorkers.push(worker);
    }

    setAppliedWorkers(uniqueOrderedAppliedWorkers);
  }, [jobApplications, workers]);

  const handleAssignWorker = useCallback(
    async (worker: User) => {
      try {
        setLoading(true);

        const application = jobApplications.find(
          (app) => app.userId === worker.id,
        );

        if (!application) {
          toast.error('No application found for this worker');
          console.error(`No application found for this worker: ${worker.id}`);
          return;
        }

        // Accept the job application.
        await respondToApplication(
          application.id,
          JobApplicationStatus.Accepted,
        );

        // Show user the success toast.
        toast.success(
          `${worker.firstName} ${worker.lastName} successfully assigned to job!`,
        );
      } catch (error) {
        console.error('Error assigning worker to job:', error);
        toast.error('Error assigning worker to job');
      } finally {
        setLoading(false);
        setOpenWorkerSearchModal(false);
      }
    },
    [
      jobApplications,
      respondToApplication,
      setLoading,
      setOpenWorkerSearchModal,
    ],
  );

  if (openWorkerSearchModal) {
    return (
      <WorkerSearchbar
        job={job}
        setLoading={setLoading}
        setOpenWorkerSearchModal={setOpenWorkerSearchModal}
      />
    );
  }

  if (jobApplications.length === 0) {
    return (
      <div className="flex h-12 w-full items-center justify-between gap-2 rounded-md bg-white p-2">
        <div className="flex items-center gap-2">
          <div className="pl-3 text-sm text-gray-600">
            No {getTerm(TemplateTerm.WORKER, false)} applied
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col space-y-2">
      {appliedWorkers.map((applyingWorker: User) => (
        <WorkerApplication
          key={applyingWorker.id}
          worker={applyingWorker}
          job={job}
          onAssignWorker={handleAssignWorker}
        />
      ))}
    </div>
  );
};

export default AppliedWorkers;
